/**
 * One mission, as a live cockpit (missions vision, section 3): the header says what it is and the
 * state once, with the live facts (Agent, Machine, Branch, Elapsed, Cost against budget) and the
 * stage stepper; the main column says what is happening now and streams the followed session's
 * activity, with anything that needs a person answered in place; the rail (under the stream on a
 * phone) holds the criteria verdicts, the files changed, the checks and the links. Goal,
 * attachments, comments and the start history fold away below. One primary action per state.
 * Errors show in a banner under the header, never a toast.
 */
import {
  ArrowLeft,
  ArrowRightLeft,
  ArrowUpRight,
  CircleCheck,
  CircleDashed,
  CircleStop,
  CircleX,
  Eye,
  Hand,
  LoaderCircle,
  MoreHorizontal,
  OctagonX,
  Pencil,
  Play,
  RotateCcw,
  Rocket,
  ShieldAlert,
  ShieldCheck,
  Square,
  Trash2,
  Copy,
  Spline,
  TriangleAlert,
} from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { failedOnModel, failoverLinesOf, NOTHING_TO_LAND_NOTE, redirectWhy } from '@orgus/protocol';
import type { BacklogItem, Id, Mission, MissionView } from '@orgus/protocol';
import {
  Button,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ErrorState,
  IconButton,
  Markdown,
  MarkdownEditor,
  Panel,
  RelativeTime,
  cn,
  copyText,
  formatClock,
  plural,
  shortPath,
  toast,
  useNow,
} from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { useCheckout, useOrgus, useProject } from '../../lib/store.ts';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { plainText } from '../../lib/plainText.ts';
import { usePageKeys } from '../agents/usePageKeys.ts';
import { AgentKindLabel } from '../adapters/AgentKind.tsx';
import { deleteMission, stopMission, updateItem, updateMission } from './data.ts';
import { usePersonName } from './people.ts';
import type { StartTarget } from './StartMissionDialog.tsx';
import { formatUsd } from './model.ts';
import { KindIcon, StatePill } from './parts.tsx';
import type { StateTone } from './parts.tsx';
import { MissionStages } from './MissionStages.tsx';
import { missionStages } from './stages.ts';
import { AskHistory, useAskCount } from '../approvals/ApprovalExpiredEvent.tsx';
import { openAnswer } from '../approvals/AnswerSheet.tsx';
import { useApproveAndResume } from '../approvals/ApproveAndResumeButton.tsx';
import { useMeId } from '../approvals/blocked.ts';
import { AttachmentsPanel } from '../attachments/AttachmentsPanel.tsx';
import { ProofGallery, proofShots } from './ProofGallery.tsx';
import { CommentsThread } from '../attachments/CommentsThread.tsx';
import { uploaderFor, useAttachmentsOn, useAttachmentsState, useResolveAttachment } from '../attachments/data.ts';
import { SessionDrawerHost } from '../sessions/SessionDrawer.tsx';
import { failureOf, fixFor } from '../sessions/failures.ts';
import { provenModels } from '../sessions/labels.ts';
import { useMissionLanding } from './run/data.ts';
import { RUN_PHASE_LABEL, checksOf, failureKind, integrationSetback, reviewedBy, focusOf, lastLaunch, nowLine, retryChange, runBranch, runPhase, runSpan } from './run/model.ts';
import { RunReview, focusReview, reviewVerdicts } from './run/RunReview.tsx';
import { RunFailure } from './run/RunFailure.tsx';
import { retryLanding, useLandingSlice } from '../landing/api.ts';
import type { RunPhase } from './run/model.ts';
import { RunFacts } from './run/RunFacts.tsx';
import { NowLine, RunStream, focusAnswer } from './run/RunStream.tsx';
import { ChangesPanel, ChecksPanel, CriteriaPanel, LinksPanel, SessionsPanel } from './run/RunRail.tsx';
import { RunSection } from './run/RunSection.tsx';

type Icon = ComponentType<{ className?: string }>;

/** The header's one status: icon plus label, a soft pill (status.md: colour only for running, needs you, failed). */
const PHASE_PILL: Record<RunPhase, { tone: StateTone; icon: Icon }> = {
  planned: { tone: 'neutral', icon: CircleDashed },
  preparing: { tone: 'working', icon: LoaderCircle },
  working: { tone: 'working', icon: LoaderCircle },
  needs: { tone: 'attention', icon: Hand },
  blocked: { tone: 'danger', icon: ShieldAlert },
  checking: { tone: 'working', icon: LoaderCircle },
  review: { tone: 'neutral', icon: Eye },
  landing: { tone: 'working', icon: LoaderCircle },
  landed: { tone: 'neutral', icon: CircleCheck },
  done: { tone: 'neutral', icon: CircleCheck },
  failed: { tone: 'danger', icon: CircleX },
  stopped: { tone: 'neutral', icon: CircleStop },
};

const SPIN: ReadonlySet<RunPhase> = new Set(['preparing', 'working', 'checking', 'landing']);
/** Phases still on their way to the landing queue: the Checks panel says when its checks will run. */
const CHECKS_AHEAD: ReadonlySet<RunPhase> = new Set(['planned', 'preparing', 'working', 'needs', 'blocked']);

export function MissionDetail({
  mission,
  view,
  item,
  projectName,
  onStartSession,
}: {
  mission: Mission;
  view: MissionView;
  item: BacklogItem | null;
  projectName: (id: Id) => string;
  /** Start another session; with `retryFrom`, set up like the last start. */
  onStartSession: (retryFrom?: StartTarget['retryFrom']) => void;
}) {
  const navigate = useNavigate();
  const nameOf = usePersonName();
  const machines = useOrgus((s) => s.machines);
  const now = useNow(5_000);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goal, setGoal] = useState(mission.goal);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [confirm, setConfirm] = useState<'stop' | 'delete' | 'close' | null>(null);
  const [openFile, setOpenFile] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [, setParams] = useSearchParams();
  const target = useMemo(() => ({ kind: 'mission' as const, id: mission.id }), [mission.id]);
  const filesOn = !!useAttachmentsState();
  const attached = useAttachmentsOn(target);
  const itemFiles = useAttachmentsOn(useMemo(() => (item ? { kind: 'item' as const, id: item.id } : null), [item?.id]));
  const resolve = useResolveAttachment();
  const upload = useMemo(() => uploaderFor(target), [target]);

  useEffect(() => {
    setGoal(mission.goal);
    setEditingGoal(false);
    setError(null);
    setFocusId(null);
    // Only when another mission opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission.id]);

  // Where it is: the shared stages, then the run phase the header and the stream speak in.
  const landing = useMissionLanding(mission.id, now);
  const stages = missionStages(mission, view, { checks: checksOf(landing), reviewed: reviewedBy(landing) });
  const phase = runPhase(mission, view, stages, landing, now);
  const live = view.sessions.filter((s) => s.session && s.session.status !== 'ended');
  const closed = !!mission.closed;
  const neverStarted = !mission.launches.length && !mission.sessionIds.length;
  const launch = lastLaunch(mission);
  const focus = view.sessions.find((s) => s.id === focusId && s.session) ?? focusOf(view);
  const focusSession = focus?.session ?? null;
  const machineId = focus?.machineId ?? launch?.machineId ?? mission.placement.machineId ?? null;
  const machine = machineId ? machines[machineId] : undefined;
  const machineName = machine?.name ?? 'its machine';
  const checkout = useCheckout(focusSession?.checkoutId ?? launch?.checkoutId ?? null);
  const branch = runBranch(mission, checkout?.branch, landing);
  const span = runSpan(mission, view, phase);
  // QA-59: finished, but Orgus could not land the branch (a merge conflict, failing checks): say that, not In review.
  const setback = phase === 'review' ? integrationSetback(mission) : null;
  const now1 = setback ? { text: `${setback.text}. ${setback.who}`, tone: 'attention' as const } : nowLine(phase, { m: mission, v: view, focus, landing, machineName });
  const nowAt = phase === 'checking' || phase === 'landing' ? (landing?.updatedAt ?? null) : focusSession && !SPIN.has(phase) ? focusSession.lastActivity : (focusSession?.lastActivity ?? launch?.at ?? null);
  const worktree = launch?.worktree ?? null;
  // Review: land the branch through the landing queue when this hub runs one and the branch is not the target itself.
  const project = useProject(mission.projectId);
  const landTarget = project?.defaultBranch || 'main';
  const landingSlice = useLandingSlice();
  const mockMode = useOrgus((s) => s.mode === 'mock');
  const canLand = (!!landingSlice || mockMode) && !!branch && branch !== landTarget;
  const failure = phase === 'failed' ? failureKind({ m: mission, v: view, landing }) : null;
  // The followed session's own failure card (Activity) carries the retry and the error: the page says it once.
  const cardFix = failure === 'session' && !!focusSession && (failureOf(focusSession)?.cause ?? 'blocked') !== 'blocked';
  // A model the account cannot use: the now line names the same retry the card's button offers.
  const sessionsById = useOrgus((s) => s.sessions);
  const cardFailure = cardFix && focusSession ? failureOf(focusSession) : null;
  const provenOnMachine = useMemo(
    () => (cardFailure?.cause === 'model-unavailable' && focusSession ? provenModels(Object.values(sessionsById).filter((x) => x.machineId === focusSession.machineId)) : []),
    [cardFailure?.cause, focusSession, sessionsById],
  );
  const modelRetry =
    cardFailure?.cause === 'model-unavailable' && focusSession
      ? fixFor(cardFailure, focusSession, { machineName, proven: provenOnMachine, now: Date.now(), clock: formatClock }).label
      : null;
  const unavailableModel = cardFailure?.model ?? focusSession?.model ?? null;
  const nowText = modelRetry ? `The model ${unavailableModel ? `${unavailableModel} ` : ''}is not available to this account. ${modelRetry}.` : now1.text;
  const reviewing = phase === 'review' || failure === 'check';
  const verdicts = reviewVerdicts(item, mission);
  const [retryingChecks, setRetryingChecks] = useState(false);
  const retryChecks = async () => {
    if (!landing) return;
    setRetryingChecks(true);
    setError(null);
    try {
      await retryLanding(landing.id);
    } catch (e) {
      setError(toUiError(e, { what: 'The checks were not run again.', next: 'Open the landing in Pipelines and retry it there.' }));
    } finally {
      setRetryingChecks(false);
    }
  };
  /** The session drawer, opened in place (?session=, and ?tab= for its diff). */
  const openSession = (id: Id, tab?: 'diff') =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('session', id);
        if (tab) n.set('tab', tab);
        else n.delete('tab');
        return n;
      },
      { preventScrollReset: true },
    );

  // Questions and plans its sessions asked, and how each was settled, in Orgus or in the session (ADR 0056).
  const askCount = useAskCount(mission.sessionIds);
  // The actions offered follow the state; data-mission-actions keeps its older names for tests and probes.
  const actionsPhase = closed ? 'closed' : neverStarted || view.state === 'planned' ? 'planned' : live.length || view.state === 'running' || view.state === 'waiting' ? 'live' : view.state === 'done' ? 'done' : 'ended';
  const pendingApproval = view.pendingApprovals[0] ?? null;
  // A session an expired or denied request stopped: Approve and resume continues it (R-HL-4).
  const blockedBy = view.sessions.find((s) => s.block && s.block.phase !== 'resuming')?.block ?? null;
  const me = useMeId();
  const redirected = blockedBy?.lead === 'handled' ? blockedBy : null;
  const resumeRedirect = useApproveAndResume(redirected?.approval.id ?? null, { alwaysConfirm: true, redirect: redirected?.redirect ?? null });
  const modelFailed = view.sessions.some((s) => s.state === 'failed' && s.session && failedOnModel(s.session));
  const retry = () => onStartSession({ launch: mission.launches[mission.launches.length - 1] ?? null, failedOnModel: modelFailed });
  /** Launch id -> what became of its session, for the start history. */
  const outcome = new Map(view.sessions.filter((s) => s.launch).map((s) => [s.launch!.id, s]));

  const save = async (patch: Parameters<typeof updateMission>[1], ok: string) => {
    setSaving(true);
    setError(null);
    try {
      await updateMission(mission.id, patch);
      toast.success(ok, mission.title);
      return true;
    } catch (e) {
      setError(toUiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveBudget = (value: number | null) => save({ budget: value }, value == null ? 'Budget removed' : `Budget set to ${formatUsd(value)}`);
  const saveHardStop = (on: boolean) => save({ budgetHardStop: on }, on ? 'Stops at its budget' : 'Warns at its budget, never stops');

  // Closing as done while sessions still run does not stop them: ask first, so it is not a surprise.
  const closeAsDone = () => (live.length ? setConfirm('close') : void save({ closed: 'done' }, 'Mission closed as done'));

  /** Accept the work: close the mission as done and move its item to Done. */
  const accept = async () => {
    if (!(await save({ closed: 'done' }, item ? 'Mission accepted, item done' : 'Mission accepted'))) return;
    if (item && item.status !== 'done' && item.status !== 'dropped') {
      try {
        await updateItem(item.id, { status: 'done' });
      } catch (e) {
        setError(toUiError(e));
      }
    }
  };

  const cmds = useMemo<Command[]>(
    () => [
      { id: 'mission.session', title: 'Start another session', subtitle: mission.title, group: 'Mission', icon: Play, shortcut: ['s'], enabled: !closed, perform: () => onStartSession() },
      { id: 'mission.retry', title: 'Retry mission', subtitle: mission.title, group: 'Mission', icon: RotateCcw, enabled: phase === 'failed' || actionsPhase === 'ended', perform: retry },
      { id: 'mission.accept', title: 'Accept and close as done', subtitle: mission.title, group: 'Mission', icon: CircleCheck, enabled: phase === 'review', perform: () => void accept() },
      { id: 'mission.close', title: 'Close mission as done', subtitle: mission.title, group: 'Mission', icon: CircleCheck, enabled: !closed, perform: closeAsDone },
      { id: 'mission.stop', title: 'Stop mission', subtitle: live.length ? `Stops ${plural(live.length, 'live session')}` : mission.title, group: 'Mission', icon: Square, enabled: !closed, perform: () => setConfirm('stop') },
      { id: 'mission.reopen', title: 'Reopen mission', subtitle: mission.title, group: 'Mission', icon: RotateCcw, enabled: closed, perform: () => void save({ closed: null }, 'Mission reopened') },
      { id: 'mission.back', title: 'Back to missions', group: 'Mission', icon: ArrowLeft, perform: () => navigate('/missions?tab=missions') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mission.id, mission.title, closed, live.length, onStartSession, phase, actionsPhase, modelFailed],
  );
  useRegisterCommands(cmds, [cmds]);
  usePageKeys({ s: () => !closed && (phase === 'failed' ? retry() : onStartSession()) });

  // One primary action for the state (missions vision, section 3). When the page body holds the
  // decision (an answer card, the blocker card, the review, a failure card with its fix), that card
  // carries the primary and the header's action is secondary, or moves to the menu.
  let primary = null;
  if (phase === 'planned')
    primary = (
      <Button variant="primary" icon={<Rocket />} shortcut={['s']} onClick={() => onStartSession()}>
        Start mission
      </Button>
    );
  else if (phase === 'needs')
    primary = (
      <Button variant="secondary" icon={<Hand />} onClick={() => focusAnswer() || (pendingApproval && openAnswer(pendingApproval.id))}>
        Answer
      </Button>
    );
  else if (phase === 'blocked')
    primary = redirected || blockedBy?.resumable ? null : (
      <Button variant="primary" icon={<RotateCcw />} shortcut={['s']} onClick={retry}>
        Retry mission
      </Button>
    );
  else if (phase === 'preparing' || phase === 'working')
    primary = (
      <Button variant="secondary" icon={<Square />} onClick={() => setConfirm('stop')}>
        Stop mission
      </Button>
    );
  else if (phase === 'review')
    primary = (
      <Button variant="secondary" icon={<Eye />} onClick={focusReview}>
        Review
      </Button>
    );
  else if (failure === 'check')
    primary = (
      <Button variant="secondary" icon={<RotateCcw />} loading={retryingChecks} onClick={() => void retryChecks()}>
        Retry checks
      </Button>
    );
  else if (phase === 'failed' && !cardFix)
    primary = (
      <Button variant="primary" icon={<RotateCcw />} shortcut={['s']} onClick={retry}>
        Retry mission
      </Button>
    );
  else if (phase === 'stopped' || phase === 'done')
    primary = (
      <Button variant="secondary" icon={<RotateCcw />} loading={saving} onClick={() => void save({ closed: null }, 'Mission reopened')}>
        Reopen mission
      </Button>
    );

  const pill = setback ? { tone: 'attention' as StateTone, icon: TriangleAlert } : PHASE_PILL[phase];
  const multi = view.sessions.length > 1;
  // Which session Activity follows: named only among several, and never when it is the mission's own title.
  const followed = multi && focusSession ? plainText(focusSession.title.replace(/^\[msn_[^\]]+\]\s*/, '')).split(/\s+Goal:/)[0] || null : null;
  const focusTitle = followed && followed !== plainText(mission.title) ? followed : null;
  const emptyLine = phase === 'planned' ? 'Start the mission to run an agent on one of your machines' : phase === 'preparing' ? `Activity shows here once ${machineName} reports the session` : 'No session reported for this mission';

  return (
    <div className="mx-auto flex w-full max-w-(--layout-width-detail) flex-col gap-6" data-run-phase={phase}>
      <header className="flex flex-col gap-4">
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
          <Link to="/missions?tab=missions" className="shrink-0 rounded-control hover:text-fg">
            Missions
          </Link>
          <span aria-hidden>/</span>
          {item?.key ? (
            <Link to={`/missions?item=${encodeURIComponent(item.id)}`} className="shrink-0 rounded-control font-mono hover:text-fg">
              {item.key}
            </Link>
          ) : (
            <span className="truncate">{projectName(mission.projectId)}</span>
          )}
          <span aria-hidden>/</span>
          <span className="shrink-0 text-fg-muted">Mission</span>
        </nav>
        {mission.incidentId && (
          <div className="flex items-center justify-between rounded-control border border-border-subtle bg-subtle p-3">
            <span className="text-caption text-fg-muted">Fixing Watch incident {mission.incidentId}</span>
            <Button size="sm" variant="secondary" icon={<ArrowUpRight />} onClick={() => navigate(`/?incident=${encodeURIComponent(mission.incidentId!)}`)}>
              Open incident
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-start gap-3">
          {/* On a phone the actions take their own row only when there is a button with words. */}
          <div className={cn('flex min-w-0 flex-1 flex-col gap-1', primary ? 'basis-full md:basis-0' : 'basis-0')}>
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {/* The kind icon stays on the title's first line, however the title wraps. */}
              <span className="flex min-w-0 max-w-full items-start gap-2">
                {item && (
                  <span className="flex h-7 shrink-0 items-center">
                    <KindIcon kind={item.kind} />
                  </span>
                )}
                <h2 className="min-w-0 text-title text-fg max-sm:line-clamp-2 sm:truncate" title={mission.title}>
                  {mission.title}
                </h2>
              </span>
              <StatePill tone={pill.tone} icon={pill.icon} label={setback?.label ?? RUN_PHASE_LABEL[phase]} variant="soft" spin={SPIN.has(phase)} />
            </div>
            <p className="line-clamp-1 text-caption text-fg-subtle" title={plainText(mission.goal)}>
              {projectName(mission.projectId)}
              <span aria-hidden> · </span>
              {plainText(mission.goal)}
            </p>
            {/* QA-63: Orgus stopped it over its hard budget: the header says why. */}
            {mission.closed?.reason === 'budget' && (
              <p className="flex min-w-0 items-center gap-1 text-caption font-medium text-status-danger" data-stop-reason="budget">
                <OctagonX className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate">Orgus stopped it. {mission.closed.note ?? 'Over its budget.'}</span>
              </p>
            )}
            {/* QA-91: the branch had no commits ahead of main: the work was already there. */}
            {mission.closed?.reason === 'nothing-to-land' && (
              <p className="flex min-w-0 items-center gap-1 text-caption font-medium text-status-success" data-close-reason="nothing-to-land">
                <CircleCheck className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate">{mission.closed.note ?? NOTHING_TO_LAND_NOTE}</span>
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2" data-mission-actions={actionsPhase}>
            {primary}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton label="More mission actions" icon={<MoreHorizontal />} variant="secondary" size="md" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!closed && !neverStarted && (
                  <DropdownMenuItem icon={<Play />} onSelect={() => onStartSession()}>
                    Start another session…
                  </DropdownMenuItem>
                )}
                {phase === 'failed' && cardFix && (
                  <DropdownMenuItem icon={<RotateCcw />} onSelect={retry}>
                    Retry mission
                  </DropdownMenuItem>
                )}
                {redirected && (
                  <DropdownMenuItem icon={<ShieldCheck />} disabled={!!resumeRedirect.reason || resumeRedirect.busy} onSelect={resumeRedirect.start}>
                    Approve and resume…
                  </DropdownMenuItem>
                )}
                {!closed && phase !== 'review' && (
                  <DropdownMenuItem icon={<CircleCheck />} onSelect={closeAsDone}>
                    Close as done
                  </DropdownMenuItem>
                )}
                {!closed && phase !== 'preparing' && phase !== 'working' && (
                  <DropdownMenuItem icon={<Square />} onSelect={() => setConfirm('stop')}>
                    {live.length ? 'Stop mission' : 'Close as stopped'}
                  </DropdownMenuItem>
                )}
                {closed && phase === 'landed' && (
                  <DropdownMenuItem icon={<RotateCcw />} onSelect={() => void save({ closed: null }, 'Mission reopened')}>
                    Reopen mission
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem icon={<Copy />} onSelect={() => void copyText(mission.id)}>
                  Copy mission id
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem icon={<Trash2 />} danger disabled={!neverStarted} onSelect={() => setConfirm('delete')}>
                  {neverStarted ? 'Delete mission' : 'Delete (only before it starts)'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Panel bodyClassName="flex flex-col gap-4">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
            <MissionStages stages={stages} />
            <Button size="sm" variant="ghost" icon={<Spline />} onClick={() => navigate(`/flows?mission=${encodeURIComponent(mission.id)}`)}>
              How it is going
            </Button>
          </div>
          <div className="border-t border-border-subtle pt-4">
            <RunFacts
              agentKind={focusSession?.agentKind}
              model={focusSession?.model ?? launch?.model ?? mission.model}
              machine={machine ? { name: machine.name, online: machine.online } : null}
              branch={branch}
              settled={phase === 'review' || phase === 'landed' || phase === 'done' || phase === 'failed' || phase === 'stopped'}
              span={span}
              spanLabel={phase === 'stopped' ? 'Stopped' : phase === 'failed' ? 'Failed' : 'Finished'}
              waitingSince={phase === 'needs' ? (focusSession?.lastActivity ?? null) : null}
              cost={view.spend.cost}
              tokens={view.spend.tokens.input + view.spend.tokens.output}
              budget={mission.budget}
              budgetUsed={view.budgetUsed}
              onBudget={saveBudget}
              hardStop={!!mission.budgetHardStop}
              onHardStop={saveHardStop}
              saving={saving}
            />
          </div>
        </Panel>
        {error && <ErrorState variant="inline" error={error} action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />}
        {resumeRedirect.answer}
        {resumeRedirect.error && <ErrorState variant="inline" error={resumeRedirect.error} action={<Button variant="ghost" size="sm" onClick={resumeRedirect.retry}>Try again</Button>} />}
        {resumeRedirect.dialog}
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <NowLine phase={phase} text={phase === 'blocked' && redirected?.redirect ? redirectWhy(redirected.redirect, me, nameOf) : nowText} tone={now1.tone} at={nowAt} />
          {failure && !cardFix && <RunFailure kind={failure} landing={landing} change={retryChange(view)} onShowSession={focusSession ? () => openSession(focusSession.id) : null} />}
          {reviewing && (
            <RunReview
              mission={mission}
              item={item}
              verdicts={verdicts}
              branch={branch}
              target={landTarget}
              canLand={canLand && failure !== 'check' && !setback}
              launch={launch}
              sessionId={focusSession?.id ?? null}
              checks={landing}
              onAccept={accept}
              accepting={saving}
            />
          )}
          <RunStream
            sessionIds={mission.sessionIds}
            focus={focusSession}
            focusTitle={focusTitle}
            machineName={machineName}
            emptyLine={emptyLine}
            settled={closed || phase === 'landed'}
            failureSaid={phase === 'failed'}
          />
          <div className="flex flex-col">
            <RunSection
              title="Goal"
              actions={!editingGoal && <IconButton label="Edit goal" icon={<Pencil />} size="xs" onClick={() => setEditingGoal(true)} />}
            >
              {editingGoal ? (
                <div className="flex flex-col gap-2">
                  <MarkdownEditor value={goal} onChange={setGoal} rows={5} maxLength={4000} autoFocus upload={filesOn ? upload : undefined} resolveAttachment={resolve} />
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setGoal(mission.goal);
                        setEditingGoal(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" variant="primary" loading={saving} disabled={!goal.trim()} onClick={async () => (await save({ goal: goal.trim() }, 'Goal saved')) && setEditingGoal(false)}>
                      Save goal
                    </Button>
                  </div>
                  <p className="text-caption text-fg-subtle">New sessions get the new goal; running ones keep theirs.</p>
                </div>
              ) : (
                <Markdown source={mission.goal} resolveAttachment={resolve} onOpenAttachment={setOpenFile} className="text-ui" />
              )}
            </RunSection>
            {askCount > 0 && (
              <RunSection title="Questions" count={askCount}>
                <AskHistory sessionIds={mission.sessionIds} />
              </RunSection>
            )}
            <RunSection title="Screenshots" count={proofShots(attached).length || undefined} defaultOpen={proofShots(attached).length > 0}>
              <ProofGallery target={target} />
            </RunSection>
            <RunSection title="Attachments" count={attached.length || undefined} defaultOpen={!!openFile}>
              <AttachmentsPanel
                target={target}
                pasteAnywhere={!editingGoal}
                openId={openFile}
                onOpenChange={setOpenFile}
                hint={`Each start copies them to the machine${itemFiles.length ? `, with the ${plural(itemFiles.length, 'file')} on its item` : ''}.`}
              />
            </RunSection>
            <RunSection title="Comments">
              <CommentsThread target={target} />
            </RunSection>
            {mission.launches.length > 0 && (
              <RunSection title="Starts" count={mission.launches.length}>
                <ul className="flex flex-col" aria-label="Starts">
                  {[...mission.launches].reverse().map((l) => {
                    const failed = l.result !== 'ok' || outcome.get(l.id)?.state === 'failed';
                    const s = outcome.get(l.id)?.session;
                    return (
                      <li key={l.id} className="flex min-h-row items-start gap-3 py-2 text-ui" data-row>
                        <span className="flex h-5 shrink-0 items-center">
                          {failed ? <CircleX className="size-4 text-status-danger" aria-label="Failed" /> : <CircleCheck className="size-4 text-fg-subtle" aria-label="Started" />}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="min-w-0 text-fg-muted">
                            <span className="text-fg">{nameOf(l.by)}</span> {l.result === 'ok' ? 'started' : 'tried to start'} a {l.mode === 'terminal' ? 'terminal' : 'headless'} session with{' '}
                            <AgentKindLabel kind={l.agentKind} className="align-bottom text-fg" />
                            {l.model ? ` (${l.model})` : ''} on {machines[l.machineId]?.name ?? 'an unknown machine'}
                            {l.worktree ? ` in ${shortPath(l.worktree, 40)}` : l.cwd ? ` in ${shortPath(l.cwd, 40)}` : ''}
                          </span>
                          {/* ADR 0049 decision 5: the agent before it failed for a reason of its own, so this start moved on. */}
                          {failoverLinesOf(l.note).map((line) => (
                            <span key={line} className="flex min-w-0 items-center gap-1 text-caption text-fg-subtle">
                              <ArrowRightLeft className="size-3 shrink-0" aria-hidden />
                              <span className="min-w-0">{line}</span>
                            </span>
                          ))}
                          {/* Why the scheduler picked this agent (ADR 0049 decision 5): one quiet line. */}
                          {l.agentReason && (
                            <span className="truncate text-caption text-fg-subtle" data-agent-reason="">
                              {l.agentReason}
                            </span>
                          )}
                          {(l.result === 'error' && l.message) || (failed && s) || (l.note && failoverLinesOf(l.note).length === 0) ? (
                            <span className="line-clamp-2 text-caption text-fg-subtle">
                              {l.result === 'error' && l.message
                                ? l.message
                                : failed && s
                                  ? failedOnModel(s)
                                    ? `This account cannot use ${l.model ?? 'that model'}. Retry with opus, sonnet or haiku.`
                                    : 'The session failed.'
                                  : `Note: ${l.note}`}
                            </span>
                          ) : null}
                        </span>
                        <RelativeTime ts={l.at} className="flex h-5 shrink-0 items-center text-caption text-fg-subtle" />
                      </li>
                    );
                  })}
                </ul>
              </RunSection>
            )}
          </div>
        </div>

        <aside className="min-w-0 lg:sticky lg:top-4 lg:self-start" aria-label="Mission details">
          <Panel bodyClassName={cn('flex flex-col gap-6')}>
            {item && item.acceptance.length > 0 && <CriteriaPanel item={item} mission={mission} />}
            {focusSession && <ChangesPanel session={focusSession} scope={worktree || launch?.branch ? 'branch' : 'working'} onOpenDiff={() => openSession(focusSession.id, 'diff')} landedSha={mission.landed?.sha ?? null} projectId={mission.projectId} />}
            {/* With no landing yet, Checks only says when they will run while the work is still on its way there. */}
            {(landing || CHECKS_AHEAD.has(phase)) && <ChecksPanel landing={landing} />}
            {multi && <SessionsPanel sessions={view.sessions} focusId={focus?.id ?? null} onFocus={setFocusId} />}
            <LinksPanel worktree={worktree} session={focusSession} item={item} onOpenSession={focusSession ? () => openSession(focusSession.id) : null} />
          </Panel>
        </aside>
      </div>

      <SessionDrawerHost order={view.sessions.flatMap((s) => (s.sessionId && s.session ? [s.sessionId] : []))} />

      <ConfirmDialog
        open={confirm === 'stop'}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={`Stop mission "${mission.title}"?`}
        description={
          live.length
            ? `Orgus stops its ${plural(live.length, 'live session')} (${live.map((s) => machines[s.machineId]?.name ?? s.machineId).filter((x, i, a) => a.indexOf(x) === i).join(', ')}) and closes the mission as stopped. Work they did not commit stays in their folders.`
            : 'No session is running. The mission closes as stopped; you can reopen it later.'
        }
        confirmLabel={live.length ? 'Stop mission' : 'Close as stopped'}
        onConfirm={async () => {
          const r = await stopMission(mission.id);
          toast.success('Mission stopped', r.message);
        }}
      />
      <ConfirmDialog
        open={confirm === 'close'}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={`Close "${mission.title}" as done?`}
        description={`${plural(live.length, 'session')} will keep running; use Stop mission to stop ${live.length === 1 ? 'it' : 'them'} too.`}
        confirmLabel="Close as done"
        tone="primary"
        onConfirm={async () => {
          await save({ closed: 'done' }, 'Mission closed as done');
        }}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={`Delete mission "${mission.title}"?`}
        description="It never started; its backlog item stays."
        confirmLabel="Delete mission"
        onConfirm={async () => {
          await deleteMission(mission.id);
          toast.success('Mission deleted', mission.title);
          navigate('/missions?tab=missions');
        }}
      />
    </div>
  );
}
